import { useCallback, useEffect, useMemo, useState } from 'react';
// MIGRATION: reads via PostgREST (`aws.from`); writes go through the
// authorised-write Lambda so every mutation lands in `auth_audit_log` and
// the Lambda enforces role=admin on these tables (see lambdas/authorised-
// write/index.ts ADMIN_ONLY_TABLES).
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import SpendTicker from '../ai/SpendTicker';
import SpendDashboard from './SpendDashboard';
// Removed 2026-04-28: Admin Panel → Reporting tab was redundant.
// The Reporting page (app/src/reporting/Reporting.jsx) is the single
// reporting hub now — admin CRUD lives there ("+ Add report", edit,
// delegate, Everyone/Mine toggle). ReportingTab.jsx is left on disk
// in case any logic needs to be salvaged later.
import Archive from '../archive/Archive';
import '../po/drawer.css';

// Activity Log uses exact times (per Mo). Today → HH:MM. Older → DD MMM HH:MM.
function fmtExact(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtRelative(ts) {
  if (!ts) return 'Never';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

const DEPARTMENTS = ['Marketing', 'Logistics', 'Finance', 'Ops', 'Product', 'Creative', 'Support', 'Management', 'Engineering', 'Leadership'];
const ROLE_BADGE = { admin: { bg: 'rgba(124,58,237,0.1)', color: '#7c3aed', label: 'Super Admin' }, user: { bg: '#f4f4f5', color: '#52525b', label: 'User' } };

// Page access — matches sidebar nav structure
const MAIN_PAGES = [
  { key: 'home', label: 'Home' }, { key: 'myday', label: 'Work' }, { key: 'stock', label: 'Stock' },
  { key: 'finance', label: 'Finance' }, { key: 'contacts', label: 'People' },
  { key: 'files', label: 'Files' }, { key: 'archive', label: 'Archive' },
];
const CONSTRUCTION_PAGES = [
  { key: 'calendar', label: 'Calendar' }, { key: 'signals', label: 'Signals' },
  { key: 'sales', label: 'Sales' }, { key: 'b2b', label: 'B2B' },
  { key: 'ads', label: 'Ads' }, { key: 'creative', label: 'Creative' },
  { key: 'retention', label: 'Retention' }, { key: 'support', label: 'Support' },
  { key: 'team', label: 'Team' }, { key: 'vault', label: 'Vault' },
  { key: 'webchanges', label: 'Web Changes' },
];
const ALL_PAGES = [...MAIN_PAGES, ...CONSTRUCTION_PAGES];
const PAGE_LABELS = Object.fromEntries(ALL_PAGES.map(p => [p.key, p.label]));
const PAGE_KEYS = ALL_PAGES.map(p => p.key);
const DEFAULT_PERMS = Object.fromEntries([
  ...MAIN_PAGES.map(p => [p.key, ['home', 'myday', 'stock', 'contacts', 'archive'].includes(p.key)]),
  ...CONSTRUCTION_PAGES.map(p => [p.key, false]),
]);

// Per-page granular actions. Each sub-key uses the `!== false` default-true
// pattern: undefined → granted; only an explicit `false` denies.
//
// Wiring status:
//   stock_write           → Catalogue.jsx + CatalogueDrawer.jsx (live)
//   stock_demand_approve  → Demand.jsx (currently still admin-role-only; flag exists but not yet enforced)
//   finance_invoices_write → InvoiceTracker.jsx (not yet enforced)
//   finance_pos_write     → PoTracker.jsx (not yet enforced)
//   finance_subs_write    → Subscriptions.jsx (not yet enforced)
//   contacts_write        → AddressBook.jsx (not yet enforced)
//
// Admins always bypass these checks regardless of flag.
const PAGE_ACTIONS = {
  stock: [
    { key: 'stock_write',           label: 'Add / edit SKUs in Catalogue' },
    { key: 'stock_demand_approve',  label: 'Approve demand drafts (commits money to suppliers)' },
  ],
  finance: [
    { key: 'finance_invoices_write', label: 'Approve / dispute / pay invoices' },
    { key: 'finance_pos_write',      label: 'Create / edit purchase orders' },
    { key: 'finance_subs_write',     label: 'Manage subscriptions' },
  ],
  contacts: [
    { key: 'contacts_write',         label: 'Add / edit / archive contacts' },
  ],
};

const ACTION_VERBS = {
  login: 'logged in', record_create: 'created a record', record_update: 'updated a record',
  record_delete: 'deleted a record', comment_posted: 'posted a comment', file_upload: 'uploaded a file',
  task_complete: 'completed a task', user_created: 'created a user', user_edited: 'edited a user',
  permission_changed: 'changed permissions', memory_added: 'added a memory', memory_edited: 'edited a memory',
  memory_deleted: 'deleted a memory', page_access_toggled: 'toggled page access', pin_reset: 'reset a PIN',
};
const ACTION_FILTERS = ['All', ...Object.keys(ACTION_VERBS), 'export'];

const KIND_COLORS = { fact: '#2563eb', decision: '#7c3aed', preference: '#d97706', pattern: '#0891b2', note: '#64748b' };

// Visibility tab — pages that can be master-hidden via Admin Panel.
// MUST stay in sync with App.jsx NB_ITEMS keys. 'home' deliberately
// excluded so an admin can't softlock themselves out of the app.
const HIDEABLE_PAGES = [
  { key: 'sales',     label: 'Sales',     group: 'daily' },
  { key: 'stock',     label: 'Stock',     group: 'daily' },
  { key: 'finance',   label: 'Finance',   group: 'daily' },
  { key: 'contacts',  label: 'Contacts',  group: 'daily' },
  { key: 'reporting', label: 'Reporting', group: 'daily' },
  { key: 'clouseau',  label: 'Clouseau',  group: 'wip' },
  { key: 'files',     label: 'Files',     group: 'wip' },
  { key: 'calendar',  label: 'Calendar',  group: 'wip' },
  { key: 'retention', label: 'Retention', group: 'wip' },
];

// Short event IDs for the Activity Log. Format: {PREFIX}-{first 8 chars
// of source uuid}. Mo can quote one back when investigating an event
// — short enough to read aloud, long enough to be unique.
//   LOG = user_activity_log row (login / general activity)
//   WRT = auth_audit_log row    (every write through authWrite Lambda)
//   CHT = staff_conversations row (LLM chat session)
function eventShortId(uuid, prefix) {
  if (!uuid) return `${prefix}-?`;
  return `${prefix}-${String(uuid).slice(0, 8)}`;
}

// ── System tab ──────────────────────────────────────────────────────
// Minimal health surface: probes PostgREST anon, classifies the result
// (healthy/degraded/error/loading), explains the cause in plain English,
// and offers a one-click "copy fix prompt" for Claude Code. Same probe
// shape as SystemStatus.jsx — kept independent so we can change either
// without coupling them. Deliberately not a monitoring dashboard.
const PROBE_SLOW_MS = 1500;

function probeStatusColor(status) {
  return { loading: '#a1a1aa', healthy: '#16a34a', degraded: '#d97706', error: '#dc2626' }[status] || '#a1a1aa';
}
function probeStatusLabel(status) {
  return { loading: 'Checking…', healthy: 'Healthy', degraded: 'Degraded', error: 'Down' }[status] || status;
}

function plainEnglishCause(probe) {
  if (probe.status === 'loading') return 'Running probe…';
  if (probe.status === 'healthy') return 'PostgREST responded with 200 in under 1.5s. Frontend reads should be working normally.';
  if (probe.status === 'degraded') return `Probe took ${probe.ms} ms — slower than the 1.5s threshold. PostgREST or its CloudFront route may be under load. Reads will still work but feel sluggish.`;
  if (probe.code === 401 || probe.code === 403) return 'PostgREST refused the request (401/403). The probe URL hits a table or view the anon role does not have SELECT on. The Tier 1 lockdown narrowed anon access; the probe URL may now point at a restricted table.';
  if (probe.code === 404) return 'PostgREST returned 404. The probe URL points at a table or view that does not exist (renamed, dropped, or schema cache stale). NOTIFY pgrst, \'reload schema\' may help.';
  if (probe.code && probe.code >= 500) return `PostgREST returned ${probe.code}. The Fargate task may be unhealthy or restarting; or the underlying RDS query failed. Check the ECS service + CloudWatch logs.`;
  if (!probe.code) return 'Network error reaching PostgREST. The CloudFront distribution, the ALB, or DNS may be down. Could also be a browser-side block (extension, network).';
  return `Unexpected response code ${probe.code}.`;
}

function buildFixPrompt(probe) {
  return [
    'Fix the moreece.com SystemStatus / Admin → System health probe.',
    '',
    'Current state:',
    `- Probe URL: ${probe.url || '(not set)'}`,
    `- HTTP status: ${probe.code != null ? probe.code : 'no response'}`,
    `- Response time: ${probe.ms != null ? probe.ms + ' ms' : '—'}`,
    `- Response body (first 500 chars): ${probe.body || '(empty)'}`,
    '',
    `Probable cause: ${plainEnglishCause(probe)}`,
    '',
    'Files to inspect:',
    '- /Users/moreece/Code/moreece.com/app/src/lib/SystemStatus.jsx (widget probe URL)',
    '- /Users/moreece/Code/moreece.com/app/src/admin/AdminPanel.jsx (System tab probe URL)',
    '- /Users/moreece/Code/moreece-aws/migrations/99-postgrest-grants.sql (anon grants)',
    '- /Users/moreece/Code/moreece-aws/lib/postgrest-stack.ts (PostgREST Fargate config)',
    '',
    'Recent context:',
    '- Tier 1 SQL (2026-04-28) revoked anon SELECT on access_codes, auth_attempts, auth_audit_log, user_activity_log, signals_items.',
    '- A staff_directory view was added as the safe anon-readable subset of access_codes.',
    '- Tier 2 Lambda CORS was locked from \'*\' to moreece origins only.',
    '',
    'Either fix the probe URL to hit a still-readable table/view, or restore the affected grant if the lockdown was too aggressive. Then rebuild the frontend (cd /Users/moreece/Code/moreece.com/app && npm run build) and run cdk deploy MoreeceFrontendStack from /Users/moreece/Code/moreece-aws/ with all three region env vars set.',
  ].join('\n');
}

function SystemTab() {
  const [probe, setProbe] = useState({ status: 'loading', url: '', code: null, ms: null, body: null, ts: null });
  const [copied, setCopied] = useState(false);

  const runProbe = useCallback(async () => {
    setProbe(p => ({ ...p, status: 'loading' }));
    const apiUrl = import.meta.env.VITE_AWS_API_URL;
    if (!apiUrl) {
      setProbe({ status: 'error', url: '(VITE_AWS_API_URL not configured)', code: null, ms: null, body: 'Build-time env var missing — frontend was built without VITE_AWS_API_URL.', ts: Date.now() });
      return;
    }
    // Mig 139 (C1) dropped `code` from staff_directory; probe by `name`.
    const url = `${apiUrl}/staff_directory?select=name&limit=1`;
    const start = performance.now();
    try {
      const res = await fetch(url, { method: 'GET' });
      const ms = Math.round(performance.now() - start);
      const text = await res.text();
      const status = !res.ok ? 'error' : (ms > PROBE_SLOW_MS ? 'degraded' : 'healthy');
      setProbe({ status, url, code: res.status, ms, body: text.slice(0, 500), ts: Date.now() });
    } catch (e) {
      setProbe({ status: 'error', url, code: null, ms: null, body: String(e && e.message ? e.message : e), ts: Date.now() });
    }
  }, []);

  useEffect(() => { runProbe(); }, [runProbe]);

  async function copyFix() {
    try {
      await navigator.clipboard.writeText(buildFixPrompt(probe));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — leave button label unchanged */
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 820 }}>
      {/* Headline status row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: probeStatusColor(probe.status), flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a' }}>PostgREST API · {probeStatusLabel(probe.status)}</div>
          <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
            {probe.ts ? `Last check ${fmtRelative(probe.ts)}` : '—'}
            {probe.ms != null ? ` · ${probe.ms} ms` : ''}
          </div>
        </div>
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={runProbe}>Re-run</button>
      </div>

      {/* Probe details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10 }}>
        <Row label="Probe URL" value={probe.url || '—'} mono />
        <Row label="HTTP status" value={probe.code != null ? String(probe.code) : '—'} mono />
        <Row label="Response time" value={probe.ms != null ? `${probe.ms} ms` : '—'} mono />
        <Row label="Response body" value={(probe.body || '—').slice(0, 240)} mono multiline />
      </div>

      {/* Plain-English cause */}
      <div style={{ padding: '14px 16px', background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10 }}>
        <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Cause</div>
        <div style={{ fontSize: 13, color: '#0a0a0a', lineHeight: 1.5 }}>{plainEnglishCause(probe)}</div>
      </div>

      {/* Copy fix prompt */}
      <div style={{ padding: '14px 16px', background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10 }}>
        <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Claude Code fix prompt</div>
        <div style={{ fontSize: 12, color: '#52525b', marginBottom: 10, lineHeight: 1.5 }}>
          Copies a self-contained prompt with the failing URL, HTTP code, body, probable cause, and the relevant files to look at. Paste into Claude Code to fix.
        </div>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={copyFix}>
          {copied ? '✓ Copied — paste into Claude Code' : 'Copy Claude Code fix prompt'}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, mono, multiline }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: multiline ? 'flex-start' : 'center' }}>
      <span style={{ fontSize: 10, color: '#71717a', width: 110, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{
        fontSize: mono ? 11 : 13,
        color: '#0a0a0a',
        fontFamily: mono ? 'JetBrains Mono, ui-monospace, monospace' : 'inherit',
        wordBreak: 'break-all',
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        flex: 1,
      }}>{value}</span>
    </div>
  );
}

export default function AdminPanel({ session, onClose }) {
  const toast = useToast();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [showPins, setShowPins] = useState({});
  // Users tab active/inactive grouping. Defaults to 'active' so the tab
  // opens decluttered — deactivated users (kept for their FK-linked
  // activity/audit history, which is why they can't be deleted) tuck
  // away under the Inactive filter instead of interleaving alphabetically.
  const [userFilter, setUserFilter] = useState('active');
  const [actFilter, setActFilter] = useState({ user: 'all', action: 'All', days: 7 });

  // Memory state — entities/facts/staff conversations are auto-managed
  // by the LLM (memory-extractor + assistant-chat Lambdas). Admins only
  // curate the freeform memories list now.
  const [memories, setMemories] = useState([]);
  const [memFilter, setMemFilter] = useState({ kind: 'all', search: '' });
  const [editMem, setEditMem] = useState(null);
  const [newMem, setNewMem] = useState(null);

  // Activity Log — unified feed across 4 sources (logins, writes, LLM
  // prompts, page activity). Loaded together for the comprehensive view.
  const [auditLog, setAuditLog] = useState([]);              // auth_audit_log — every write via Lambda
  const [llmConversations, setLlmConversations] = useState([]);  // staff_conversations + staff_messages
  const [exportActivity, setExportActivity] = useState([]);  // forecaster_activity rows tagged 'Excel export: ...'
  // Per-user expand state for the Activity Log. Set of user codes that
  // are currently open. All collapsed by default — click a card header
  // to expand its sessions + events.
  const [expandedUsers, setExpandedUsers] = useState(() => new Set());

  // Tier 1 lockdown (2026-04-28): anon lost SELECT on access_codes,
  // auth_attempts, auth_audit_log, user_activity_log. Users tab now reads
  // the safe `staff_directory` view; Activity Log tab calls the
  // admin-PIN-gated `moreece-admin-activity-feed` Lambda which connects
  // as postgres master and returns all three tables in one shot.
  const ADMIN_ACTIVITY_FEED_URL = import.meta.env.VITE_AWS_ADMIN_ACTIVITY_FEED_URL;

  // Mig 139 (C1, 2026-05-10): staff_directory now omits `code` (the
  // PIN/auth credential) from anon. Admin Users tab needs codes to
  // edit/deactivate/email a row, so route through admin_staff_directory()
  // — gated server-side on jwt_claim('user_role') = 'admin'.
  const loadUsers = useCallback(async () => { const { data } = await aws.rpc('admin_staff_directory'); setUsers((data || []).slice().sort((a,b) => (a.name||'').localeCompare(b.name||''))); }, []);

  // Single fetch: returns { audit_log, activity, llm_conversations }.
  // Bad/missing env var → silently empty; the tab will still render the
  // "no activity in this range" empty state and the Diagnosis banner can
  // surface the configuration gap.
  const loadActivityFeed = useCallback(async () => {
    if (!ADMIN_ACTIVITY_FEED_URL) {
      setAuditLog([]); setActivity([]); setLlmConversations([]);
      return;
    }
    try {
      const res = await fetch(ADMIN_ACTIVITY_FEED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userPin: session?.code, days: actFilter.days }),
      });
      if (!res.ok) {
        console.warn('admin-activity-feed', res.status);
        setAuditLog([]); setActivity([]); setLlmConversations([]);
        return;
      }
      const data = await res.json();
      setAuditLog(data.audit_log || []);
      setActivity(data.activity || []);
      setLlmConversations(data.llm_conversations || []);
    } catch (e) {
      console.warn('admin-activity-feed error:', e?.message || e);
      setAuditLog([]); setActivity([]); setLlmConversations([]);
    }
  }, [ADMIN_ACTIVITY_FEED_URL, session?.code, actFilter.days]);

  // Excel export events — logged straight into forecaster_activity by
  // logExportActivity() (app/src/lib/exportActivityLog.js) since that
  // table is the only anon-writable activity table with no foreign-key
  // requirement, so it doubles as the generic "export log". Read directly
  // via PostgREST (anon SELECT is open on this table) rather than through
  // the admin-activity-feed Lambda.
  const loadExportActivity = useCallback(async () => {
    const since = new Date(Date.now() - actFilter.days * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await aws.from('forecaster_activity')
      .select('*').ilike('action', 'Excel export:%').gte('created_at', since)
      .order('created_at', { ascending: false }).limit(200);
    if (error) { console.warn('export-activity-log', error.message); setExportActivity([]); return; }
    setExportActivity(data || []);
  }, [actFilter.days]);

  const loadMemories = useCallback(async () => { const { data } = await aws.from('mo_memories').select('*').is('superseded_at', null).order('created_at', { ascending: false }).limit(100); setMemories(data || []); }, []);

  // Visibility tab — master-hidden page rows. Each row = one hidden page.
  // Empty list = nothing hidden. Loaded on tab open and after each toggle.
  const [hiddenRows, setHiddenRows] = useState([]);
  const [hideBusy, setHideBusy] = useState(null); // page_key currently being toggled
  const loadHidden = useCallback(async () => {
    const { data } = await aws.from('master_hidden_pages').select('*').order('hidden_at', { ascending: false });
    setHiddenRows(data || []);
  }, []);
  const togglePageHidden = useCallback(async (pageKey, currentlyHidden) => {
    setHideBusy(pageKey);
    try {
      if (currentlyHidden) {
        await authWrite(session, 'master_hidden_pages', 'delete', null, { column: 'page_key', value: pageKey });
      } else {
        await authWrite(session, 'master_hidden_pages', 'insert', { page_key: pageKey, hidden_by: session.code });
      }
      await loadHidden();
      // Notify App.jsx so the nav refreshes immediately without page reload.
      window.dispatchEvent(new CustomEvent('fon-pages-visibility-changed'));
      toast?.show?.(currentlyHidden ? `Restored: ${pageKey}` : `Hidden: ${pageKey}`);
    } catch (e) {
      toast?.show?.(`Failed: ${e?.message || 'unknown error'}`);
    } finally {
      setHideBusy(null);
    }
  }, [session, loadHidden, toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === 'activity') { loadActivityFeed(); loadExportActivity(); } }, [tab, loadActivityFeed, loadExportActivity]);
  useEffect(() => { if (tab === 'memory') { loadMemories(); } }, [tab, loadMemories]);
  useEffect(() => { if (tab === 'visibility') { loadHidden(); } }, [tab, loadHidden]);

  // Resolve actor_code to name — NEVER show raw PIN
  function resolveUser(code) {
    if (!code) return 'System';
    // Export events come from forecaster_activity, which has no actor_code
    // column — synthesized as `export:<name>` so exporters still group
    // per-person in the Timeline (see unifiedFeed below).
    if (code.startsWith('export:')) return code.slice(7) || 'Unknown';
    const u = users.find(x => x.code === code);
    if (u) return u.name;
    // If code looks like a PIN (all digits), return "Unknown" not the PIN
    if (/^\d+$/.test(code)) return 'Unknown';
    return code;
  }

  // ── Unified activity feed ──────────────────────────────────────────
  // Combines 3 source tables into one chronological event stream. Each
  // event has the same shape so the UI can render them uniformly:
  //   { kind, actor_code, when, label, detail, ip, success }
  //
  // kind:
  //   'login'    — from user_activity_log (action_type='login')
  //   'write'    — from auth_audit_log (table writes via Lambda)
  //   'llm'      — from staff_conversations (LLM chat sessions)
  //   'activity' — other entries from user_activity_log (catch-all)
  const unifiedFeed = useMemo(() => {
    const events = [];
    activity.forEach(a => {
      events.push({
        eventId: eventShortId(a.id, a.action_type === 'login' ? 'LOG' : 'ACT'),
        sourceTable: 'user_activity_log',
        sourceId: a.id,
        kind: a.action_type === 'login' ? 'login' : 'activity',
        actor_code: a.actor_code,
        when: a.created_at,
        label: ACTION_VERBS[a.action_type] || (a.action_type || '').replace(/_/g, ' '),
        detail: a.resource_label || null,
        ip: a.ip_address || null,
        success: true,
      });
    });
    auditLog.forEach(w => {
      events.push({
        eventId: eventShortId(w.id, 'WRT'),
        sourceTable: 'auth_audit_log',
        sourceId: w.id,
        kind: 'write',
        actor_code: w.user_pin,
        when: w.created_at,
        label: `${w.action} on ${w.tbl}`,
        detail: w.detail || null,
        ip: null,
        success: w.success,
      });
    });
    llmConversations.forEach(c => {
      events.push({
        eventId: eventShortId(c.id, 'CHT'),
        sourceTable: 'staff_conversations',
        sourceId: c.id,
        kind: 'llm',
        actor_code: c.staff_user_id,
        when: c.started_at,
        label: `chat with Mo · ${c.message_count || 0} message${c.message_count === 1 ? '' : 's'}`,
        detail: c.summary || (c.topic_tags || []).join(', ') || null,
        ip: null,
        success: true,
      });
    });
    exportActivity.forEach(x => {
      events.push({
        eventId: eventShortId(x.id, 'EXP'),
        sourceTable: 'forecaster_activity',
        sourceId: x.id,
        kind: 'export',
        actor_code: x.actor_name ? `export:${x.actor_name}` : null,
        when: x.created_at,
        label: (x.action || '').replace(/^Excel export:\s*/, 'exported '),
        detail: x.detail || null,
        ip: null,
        success: true,
      });
    });
    return events.sort((a, b) => new Date(b.when) - new Date(a.when));
  }, [activity, auditLog, llmConversations, exportActivity]);

  // Filter by user / kind / time window.
  const filteredActivity = useMemo(() => {
    let list = unifiedFeed;
    if (actFilter.user !== 'all') list = list.filter(e => e.actor_code === actFilter.user);
    if (actFilter.action !== 'All') {
      const want = actFilter.action.toLowerCase();
      list = list.filter(e => e.kind === want || e.label?.includes(actFilter.action));
    }
    const cutoff = new Date(Date.now() - actFilter.days * 86400000).toISOString();
    return list.filter(e => (e.when || '') >= cutoff);
  }, [unifiedFeed, actFilter]);

  // Per-user grouping with simple session inference (a session ends after
  // a 30-min gap of inactivity — matches the 30m idle logout cap in
  // project_auth_security_posture).
  const groupedByUser = useMemo(() => {
    const byUser = new Map();
    filteredActivity.forEach(e => {
      const code = e.actor_code || 'system';
      if (!byUser.has(code)) byUser.set(code, []);
      byUser.get(code).push(e);
    });
    // Within each user, infer sessions: first login starts one, 30+ min gap ends it.
    const out = [];
    byUser.forEach((events, code) => {
      // events come in already-sorted (newest first); sort oldest-first for session walk.
      const ascending = [...events].reverse();
      const sessions = [];
      let cur = null;
      ascending.forEach(e => {
        const t = new Date(e.when).getTime();
        if (!cur || (t - new Date(cur.lastTs).getTime()) > 30 * 60 * 1000) {
          // Start new session
          if (cur) sessions.push(cur);
          cur = { startTs: e.when, lastTs: e.when, events: [e] };
        } else {
          cur.lastTs = e.when;
          cur.events.push(e);
        }
      });
      if (cur) sessions.push(cur);
      out.push({
        code,
        name: resolveUser(code),
        eventCount: events.length,
        sessions: sessions.reverse(),  // newest session first
      });
    });
    // Order users by most-recent-event desc.
    return out.sort((a, b) => {
      const aLast = a.sessions[0]?.lastTs || '';
      const bLast = b.sessions[0]?.lastTs || '';
      return bLast.localeCompare(aLast);
    });
  }, [filteredActivity, users]);   // eslint-disable-line react-hooks/exhaustive-deps

  const actStats = useMemo(() => {
    const counts = { login: 0, write: 0, llm: 0, activity: 0, export: 0 };
    filteredActivity.forEach(e => { counts[e.kind] = (counts[e.kind] || 0) + 1; });
    const failedWrites = filteredActivity.filter(e => e.kind === 'write' && !e.success).length;
    return [
      { label: 'Logins', count: counts.login || 0 },
      { label: 'Writes', count: counts.write || 0 },
      { label: 'LLM Chats', count: counts.llm || 0 },
      { label: 'Exports', count: counts.export || 0 },
      { label: 'Failed', count: failedWrites },
      { label: 'Other', count: counts.activity || 0 },
    ];
  }, [filteredActivity]);

  // Memory needs-verify: FACT at 100% confidence, older than 30 days
  function needsVerify(m) {
    if (m.kind !== 'fact' || m.confidence < 1.0) return false;
    const age = Date.now() - new Date(m.created_at).getTime();
    return age > 30 * 86400000;
  }

  async function saveUser() {
    if (!editUser?.name?.trim() || !editUser?.code?.trim()) return;
    const payload = {
      name: editUser.name.trim(), code: editUser.code.trim(), role: editUser.role || 'user',
      department: editUser.department || null, title: editUser.title || null,
      timezone: editUser.timezone || 'Australia/Sydney',
      active: editUser.active !== false, permissions: editUser.permissions || {},
    };
    try {
      if (editUser._isNew) {
        payload.created_by = session.code;
        await authWrite(session, 'access_codes', 'insert', payload);
      } else {
        await authWrite(session, 'access_codes', 'update', payload,
          { column: 'code', value: editUser._origCode || editUser.code });
      }
    } catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
    setEditUser(null); loadUsers();
  }

  async function deleteUser(user) {
    if (user.code === session.code) return;
    if (!confirm(`Permanently delete ${user.name}?`)) return;
    try {
      await authWrite(session, 'access_codes', 'delete', null, { column: 'code', value: user.code });
    } catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
    loadUsers();
  }

  async function toggleActive(user) {
    if (user.code === session.code) return;
    try {
      await authWrite(session, 'access_codes', 'update', { active: !user.active },
        { column: 'code', value: user.code });
    } catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
    loadUsers();
  }

  function openNewUser() {
    setEditUser({ _isNew: true, name: '', code: '', role: 'user', department: '', title: '', active: true, permissions: { ...DEFAULT_PERMS } });
  }
  function openEditUser(u) {
    setEditUser({ ...u, _origCode: u.code, permissions: u.permissions || {} });
  }

  // Active/inactive split for the Users tab. `users` is already alpha-sorted
  // by loadUsers; we just partition + count. 'all' shows active first, then an
  // Inactive divider, then the off users (dimmed) so the grouping stays visible.
  const userCounts = useMemo(() => ({
    active: users.filter(u => u.active !== false).length,
    inactive: users.filter(u => u.active === false).length,
    all: users.length,
  }), [users]);
  const activeUsers = useMemo(() => users.filter(u => u.active !== false), [users]);
  const inactiveUsers = useMemo(() => users.filter(u => u.active === false), [users]);

  // One table row per user. Shared between the active and inactive groups so
  // the two sections stay identical. `dimmed` softens deactivated rows.
  const renderUserRow = (u, dimmed = false) => {
    const rb = ROLE_BADGE[u.role] || ROLE_BADGE.user;
    const perms = u.permissions || {};
    return (
      <tr key={u.code} className="fc-row-hover" style={dimmed ? { opacity: 0.6 } : undefined}>
        <td className="fc-td" style={{ fontWeight: 600 }}>{u.name}</td>
        <td className="fc-td" style={{ fontFamily: 'var(--font-mono)', cursor: 'pointer' }} onClick={() => setShowPins(p => ({ ...p, [u.code]: !p[u.code] }))}>
          {showPins[u.code] ? u.code : String.fromCharCode(9679).repeat(u.code?.length || 4)}
        </td>
        <td className="fc-td">{u.department || '--'}</td>
        <td className="fc-td">{u.title || '--'}</td>
        <td className="fc-td"><span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: rb.bg, color: rb.color, fontWeight: 600 }}>{rb.label}</span></td>
        <td className="fc-td"><span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: u.active ? 'rgba(22,163,74,0.1)' : '#f4f4f5', color: u.active ? '#16a34a' : '#a1a1aa' }}>{u.active ? 'Active' : 'Off'}</span></td>
        <td className="fc-td"><div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>{PAGE_KEYS.filter(k => perms[k] !== false).slice(0, 5).map(k => <span key={k} style={{ fontSize: 8, padding: '1px 4px', borderRadius: 4, background: '#f4f4f5', color: '#a1a1aa' }}>{PAGE_LABELS[k]}</span>)}{PAGE_KEYS.filter(k => perms[k] !== false).length > 5 && <span style={{ fontSize: 8, color: '#a1a1aa' }}>+{PAGE_KEYS.filter(k => perms[k] !== false).length - 5}</span>}</div></td>
        <td className="fc-td" style={{ fontSize: 11, color: '#a1a1aa' }}>{fmtRelative(u.last_login_at)}</td>
        <td className="fc-td"><div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-secondary btn-sm" style={{ padding: '1px 6px', fontSize: 10 }} onClick={() => openEditUser(u)}>Edit</button>
          {u.code !== session.code && <button className="btn btn-secondary btn-sm" style={{ padding: '1px 6px', fontSize: 10 }} onClick={() => toggleActive(u)}>{u.active ? 'Off' : 'On'}</button>}
          {u.code !== session.code && <button className="btn btn-secondary btn-sm" style={{ padding: '1px 6px', fontSize: 10, color: '#dc2626' }} onClick={() => deleteUser(u)}>Del</button>}
        </div></td>
      </tr>
    );
  };

  function toggleGroupPerms(group, checked) {
    const keys = group.map(p => p.key);
    setEditUser(f => {
      const perms = { ...f.permissions };
      keys.forEach(k => { perms[k] = checked; });
      return { ...f, permissions: perms };
    });
  }

  // Esc closes modal
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') { if (editUser) setEditUser(null); else onClose(); } }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editUser, onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />

      <div style={{ position: 'relative', zIndex: 1, flex: 1, background: '#fff', marginTop: 40, borderTopLeftRadius: 20, borderTopRightRadius: 20, boxShadow: '0 -8px 40px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'assistantSlideUp 0.25s ease-out' }}>
        <div style={{ padding: '20px 32px', borderBottom: '1px solid #e4e4e7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Admin Control Panel</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Super Admin access only</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>x</button>
        </div>

        <div className="fc-subtabs" style={{ padding: '0 32px' }}>
          <button className={`fc-subtab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>Users</button>
          <button className={`fc-subtab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity Log</button>
          <button className={`fc-subtab ${tab === 'memory' ? 'active' : ''}`} onClick={() => setTab('memory')}>Memory</button>
          {/* Reporting tab removed 2026-04-28 — the dedicated Reporting page is the single hub. */}
          <button className={`fc-subtab ${tab === 'spend' ? 'active' : ''}`} onClick={() => setTab('spend')}>Spend</button>
          <button className={`fc-subtab ${tab === 'archive' ? 'active' : ''}`} onClick={() => setTab('archive')}>Archive</button>
          <button className={`fc-subtab ${tab === 'visibility' ? 'active' : ''}`} onClick={() => setTab('visibility')}>Visibility</button>
          <button className={`fc-subtab ${tab === 'system' ? 'active' : ''}`} onClick={() => setTab('system')}>System</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>

          {/* ── USERS ── */}
          {tab === 'users' && (<>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
              {/* Active/inactive grouping. Users can't be hard-deleted once they
                  have activity/audit history (FK), so deactivate is the real
                  offboarding path — this filter keeps off users out of the way. */}
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { key: 'active',   label: 'Active',   count: userCounts.active },
                  { key: 'inactive', label: 'Inactive', count: userCounts.inactive },
                  { key: 'all',      label: 'All',      count: userCounts.all },
                ].map(f => (
                  <button
                    key={f.key}
                    className={`btn btn-sm ${userFilter === f.key ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: 11 }}
                    onClick={() => setUserFilter(f.key)}
                  >{f.label} {f.count}</button>
                ))}
              </div>
              <button className="btn btn-primary btn-sm" onClick={openNewUser}>+ New User</button>
            </div>
            <table className="fc-table" style={{ fontSize: 12 }}>
              <thead><tr>
                <th className="fc-th">Name</th><th className="fc-th" style={{ width: 80 }}>PIN</th>
                <th className="fc-th" style={{ width: 90 }}>Dept</th><th className="fc-th" style={{ width: 100 }}>Title</th>
                <th className="fc-th" style={{ width: 90 }}>Role</th><th className="fc-th" style={{ width: 60 }}>Status</th>
                <th className="fc-th" style={{ width: 120 }}>Pages</th><th className="fc-th" style={{ width: 85 }}>Last Login</th>
                <th className="fc-th" style={{ width: 100 }}>Actions</th>
              </tr></thead>
              <tbody>
                {userFilter === 'active' && activeUsers.map(u => renderUserRow(u))}
                {userFilter === 'inactive' && inactiveUsers.map(u => renderUserRow(u, true))}
                {userFilter === 'all' && (<>
                  {activeUsers.map(u => renderUserRow(u))}
                  {inactiveUsers.length > 0 && (
                    <tr>
                      <td colSpan={9} style={{ padding: '10px 8px 4px', fontSize: 10, fontWeight: 600, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid #e4e4e7' }}>
                        Inactive · {inactiveUsers.length}
                      </td>
                    </tr>
                  )}
                  {inactiveUsers.map(u => renderUserRow(u, true))}
                </>)}
              </tbody>
            </table>
            {((userFilter === 'active' && activeUsers.length === 0) ||
              (userFilter === 'inactive' && inactiveUsers.length === 0)) && (
              <div style={{ padding: 16, color: '#a1a1aa', fontSize: 12 }}>No {userFilter} users.</div>
            )}
          </>)}

          {/* ── ACTIVITY LOG ──
              Reads come from moreece-admin-activity-feed Lambda (admin-PIN
              gated). When VITE_AWS_ADMIN_ACTIVITY_FEED_URL is unset (e.g.
              local dev pre-deploy), loadActivityFeed silently returns empty
              and the by-user view shows "No activity in this range." */}
          {tab === 'activity' && !ADMIN_ACTIVITY_FEED_URL && (
            <div style={{ padding: '24px 20px', maxWidth: 640, margin: '0 auto 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#9a3412', marginBottom: 4 }}>Activity feed not configured</div>
              <div style={{ fontSize: 12, color: '#7c2d12', lineHeight: 1.5 }}>
                <code style={{ background: '#ffedd5', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>VITE_AWS_ADMIN_ACTIVITY_FEED_URL</code> is not set in this build. Deploy the admin-activity-feed Lambda + frontend to populate.
              </div>
            </div>
          )}
          {tab === 'activity' && (<>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
              {actStats.map(s => (
                <div key={s.label} style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: s.label === 'Failed' && s.count > 0 ? '#dc2626' : '#0a0a0a' }}>{s.count}</div>
                  <div style={{ fontSize: 10, color: '#a1a1aa' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <select className="form-select" style={{ width: 140, fontSize: 11 }} value={actFilter.user} onChange={e => setActFilter(f => ({ ...f, user: e.target.value }))}>
                <option value="all">All Users</option>
                {users.map(u => <option key={u.code} value={u.code}>{u.name}</option>)}
              </select>
              <select className="form-select" style={{ width: 140, fontSize: 11 }} value={actFilter.action} onChange={e => setActFilter(f => ({ ...f, action: e.target.value }))}>
                <option value="All">All Kinds</option>
                <option value="login">Logins only</option>
                <option value="write">Writes only</option>
                <option value="llm">LLM chats only</option>
                <option value="activity">Other activity</option>
              </select>
              {[1, 7, 30, 90].map(d => (
                <button key={d} className={`btn btn-sm ${actFilter.days === d ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 10 }} onClick={() => setActFilter(f => ({ ...f, days: d }))}>{d === 1 ? 'Today' : `${d}d`}</button>
              ))}
              <span style={{ fontSize: 11, color: '#a1a1aa', marginLeft: 'auto' }}>{filteredActivity.length} entries</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {groupedByUser.length === 0 && <div style={{ padding: 16, color: '#a1a1aa', fontSize: 12 }}>No activity in this range.</div>}
              {groupedByUser.map(g => {
                const lastTs = g.sessions[0]?.lastTs;
                const lastEvent = g.sessions[0]?.events[g.sessions[0].events.length - 1];
                const isOpen = expandedUsers.has(g.code);
                const toggle = () => setExpandedUsers(prev => {
                  const next = new Set(prev);
                  if (next.has(g.code)) next.delete(g.code); else next.add(g.code);
                  return next;
                });
                return (
                  <div key={g.code} style={{ border: '1px solid #e4e4e7', borderRadius: 12, overflow: 'hidden' }}>
                    {/* Collapsible header. Click anywhere on the row to toggle. */}
                    <button
                      type="button"
                      onClick={toggle}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', background: '#fafafa',
                        borderBottom: isOpen ? '1px solid #e4e4e7' : 'none',
                        border: 'none', width: '100%', textAlign: 'left',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                      aria-expanded={isOpen}
                    >
                      <span style={{
                        fontSize: 10, color: '#a1a1aa', width: 12, flexShrink: 0,
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 120ms',
                      }}>▸</span>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: `hsl(${(g.name.charCodeAt(0) * 37) % 360}, 45%, 60%)`, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{g.name.charAt(0)}</div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a' }}>{g.name}</span>
                      <span style={{ fontSize: 11, color: '#a1a1aa' }}>· {g.eventCount} event{g.eventCount === 1 ? '' : 's'} · {g.sessions.length} session{g.sessions.length === 1 ? '' : 's'}</span>
                      {lastEvent && (
                        <span style={{ fontSize: 11, color: '#52525b', marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                          · last did <span style={{ color: '#7c3aed' }}>{lastEvent.label}</span>
                        </span>
                      )}
                      {lastTs && <span style={{ fontSize: 10, color: '#a1a1aa', marginLeft: 'auto', fontFamily: 'JetBrains Mono, ui-monospace, monospace', flexShrink: 0 }}>last active {fmtExact(lastTs)}</span>}
                    </button>

                    {/* Sessions only render when expanded. */}
                    {isOpen && g.sessions.map((s, si) => {
                      const durMin = Math.max(0, Math.round((new Date(s.lastTs) - new Date(s.startTs)) / 60000));
                      return (
                        <div key={si} style={{ padding: '8px 14px', borderTop: si > 0 ? '1px dashed #e4e4e7' : 'none' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                            Session · {fmtExact(s.startTs)} · {durMin} min · {s.events.length} event{s.events.length === 1 ? '' : 's'}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {s.events.map((e, i) => <ActivityRow key={e.eventId || i} event={e} resolveUser={resolveUser} compact />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>)}

          {/* ── MEMORY ── */}
          {tab === 'memory' && (<>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="form-select" style={{ width: 120, fontSize: 11 }} value={memFilter.kind} onChange={e => setMemFilter(f => ({ ...f, kind: e.target.value }))}>
                <option value="all">All Kinds</option>
                {['fact', 'decision', 'preference', 'pattern', 'note'].map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <input className="form-input" placeholder="Search memories..." style={{ maxWidth: 200, fontSize: 11 }} value={memFilter.search} onChange={e => setMemFilter(f => ({ ...f, search: e.target.value }))} />
              <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={() => setNewMem({ kind: 'fact', content: '', context: '' })}>+ Add Memory</button>
              <span style={{ fontSize: 11, color: '#a1a1aa', marginLeft: 'auto' }}>{memories.length} active</span>
            </div>

            {newMem && (
              <div style={{ background: 'rgba(124,58,237,0.04)', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Add Memory</div>
                <select className="form-select" style={{ width: 120, fontSize: 11, marginBottom: 8 }} value={newMem.kind} onChange={e => setNewMem(f => ({ ...f, kind: e.target.value }))}>
                  {['fact', 'decision', 'preference', 'pattern', 'note'].map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <input className="form-input" style={{ width: '100%', fontSize: 12, marginBottom: 6 }} placeholder="Content" value={newMem.content} onChange={e => setNewMem(f => ({ ...f, content: e.target.value }))} />
                <input className="form-input" style={{ width: '100%', fontSize: 12, marginBottom: 8 }} placeholder="Context (optional)" value={newMem.context} onChange={e => setNewMem(f => ({ ...f, context: e.target.value }))} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={async () => {
                    if (!newMem.content?.trim()) return;
                    try {
                      await authWrite(session, 'mo_memories', 'insert',
                        { kind: newMem.kind, content: newMem.content.trim(), context: newMem.context?.trim() || null, confidence: 1.0 });
                    } catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
                    setNewMem(null); loadMemories();
                  }}>Save</button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setNewMem(null)}>Cancel</button>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Memories</div>
              {memories.filter(m => memFilter.kind === 'all' || m.kind === memFilter.kind).filter(m => !memFilter.search || m.content.toLowerCase().includes(memFilter.search.toLowerCase())).map(m => {
                const verify = needsVerify(m);
                return (
                  <div key={m.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8, background: '#fafafa', marginBottom: 4, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: KIND_COLORS[m.kind] || '#64748b', color: '#fff', textTransform: 'uppercase', flexShrink: 0, marginTop: 2 }}>{m.kind}</span>
                    {verify && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', flexShrink: 0, marginTop: 4 }} title="Needs verification" />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {editMem?.id === m.id ? (
                        <div>
                          <input className="form-input" style={{ width: '100%', fontSize: 12, marginBottom: 4 }} value={editMem.content} onChange={e => setEditMem(f => ({ ...f, content: e.target.value }))} />
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-primary btn-sm" style={{ fontSize: 9 }} onClick={async () => {
                              try { await authWrite(session, 'mo_memories', 'update', { content: editMem.content }, { column: 'id', value: m.id }); }
                              catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
                              setEditMem(null); loadMemories();
                            }}>Save</button>
                            <button className="btn btn-secondary btn-sm" style={{ fontSize: 9 }} onClick={() => setEditMem(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, color: '#0a0a0a', lineHeight: 1.4 }}>{m.content}</div>
                          {m.context && <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 2 }}>{m.context}</div>}
                          {m.entity_refs?.length > 0 && <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>{m.entity_refs.map((e, i) => <span key={i} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(124,58,237,0.08)', color: '#7c3aed' }}>{e}</span>)}</div>}
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 2, flexShrink: 0, alignItems: 'center' }}>
                      <span style={{ fontSize: 9, color: '#a1a1aa', marginRight: 4 }}>{m.confidence ? Math.round(m.confidence * 100) + '%' : ''}</span>
                      {verify && <button className="btn btn-secondary btn-sm" style={{ padding: '0 4px', fontSize: 9, color: '#d97706' }} onClick={async () => {
                        try { await authWrite(session, 'mo_memories', 'update', { confidence: 0.95 }, { column: 'id', value: m.id }); }
                        catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
                        loadMemories();
                      }}>Verify</button>}
                      <button className="btn btn-secondary btn-sm" style={{ padding: '0 4px', fontSize: 9 }} onClick={() => setEditMem({ id: m.id, content: m.content })}>Edit</button>
                      <button className="btn btn-secondary btn-sm" style={{ padding: '0 4px', fontSize: 9, color: '#dc2626' }} onClick={async () => {
                        if (!confirm('Delete?')) return;
                        try { await authWrite(session, 'mo_memories', 'update', { superseded_at: new Date().toISOString() }, { column: 'id', value: m.id }); }
                        catch (e) { const { title, body } = humanizeError(e); toast.error(title, body); return; }
                        loadMemories();
                      }}>Del</button>
                    </div>
                    <span style={{ fontSize: 9, color: '#a1a1aa', flexShrink: 0 }}>{fmtRelative(m.created_at)}</span>
                  </div>
                );
              })}
              {memories.length === 0 && <div style={{ fontSize: 12, color: '#a1a1aa', padding: 16 }}>No memories yet.</div>}
            </div>

            {/* Entities, Entity Facts, Staff Conversations all hidden —
                they're auto-populated by the LLM (memory-extractor Lambda)
                and don't need admin curation. The data still flows
                underneath to memory-retrieve → assistant-chat. */}
          </>)}

          {/* Duties + Role Profiles tabs removed — empty + the briefing
              Lambda already falls back to user.department when there's
              no role_profile. Tables retained in the DB for future use. */}

          {/* ── REPORTING ── Recurring report assignments per staffer
              (table report_assignments, migration 93). Mo creates and
              assigns; the table tracks cadence + streak + due state. */}
          {/* Reporting tab render removed 2026-04-28 — reporting is now only on the Reporting page + Home cards. */}

          {/* ── SPEND ── Full-page dashboard. Replaces the dropdown-style
              SpendTicker that lived here previously — kept the import in
              case the topbar version returns. */}
          {tab === 'spend' && <SpendDashboard session={session} users={users} />}

          {/* ── ARCHIVE ── Soft-deleted POs / invoices / contacts. Was
              its own nav surface; moved in here 2026-04-26 since only
              admins use it. */}
          {tab === 'archive' && <Archive session={session} />}

          {/* ── VISIBILITY ── Master-hide nav pages from EVERYONE incl
              admin (Mo). Per-row in master_hidden_pages = hidden. Empty
              = visible. Toggle dispatches a window event so App.jsx
              re-fetches and refreshes the nav without a page reload. */}
          {tab === 'visibility' && (() => {
            const hiddenSet = new Set(hiddenRows.map(r => r.page_key));
            function row(p) {
              const hidden = hiddenSet.has(p.key);
              const meta = hiddenRows.find(r => r.page_key === p.key);
              const busy = hideBusy === p.key;
              return (
                <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f4f4f5', opacity: busy ? 0.5 : 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: hidden ? '#a1a1aa' : '#18181b' }}>{p.label}</div>
                    <div style={{ fontSize: 10, color: '#a1a1aa', fontFamily: 'var(--font-mono)' }}>
                      {p.key}
                      {hidden && meta && <span style={{ marginLeft: 8 }}>· hidden {fmtRelative(meta.hidden_at)} by {resolveUser(meta.hidden_by) || meta.hidden_by || 'unknown'}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => togglePageHidden(p.key, hidden)}
                    style={{ padding: '4px 12px', fontSize: 11, minWidth: 84, background: hidden ? '#fee2e2' : '#dcfce7', color: hidden ? '#7f1d1d' : '#14532d', border: 'none' }}
                  >
                    {busy ? '…' : hidden ? 'Hidden' : 'Visible'}
                  </button>
                </div>
              );
            }
            const daily = HIDEABLE_PAGES.filter(p => p.group === 'daily');
            const wip = HIDEABLE_PAGES.filter(p => p.group === 'wip');
            return (
              <div>
                <div style={{ fontSize: 12, color: '#52525b', marginBottom: 16, padding: '12px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8 }}>
                  <strong style={{ color: '#854d0e' }}>Master hide:</strong> toggling a page off removes it from the nav for <em>everyone</em>, including you. Home is excluded. Use this to declutter, not to lock people out — per-user permissions live on the Users tab.
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#71717a', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 14px' }}>Daily-use pages</div>
                <div style={{ border: '1px solid #e4e4e7', borderRadius: 8, marginBottom: 16 }}>{daily.map(row)}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#71717a', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 14px' }}>In development</div>
                <div style={{ border: '1px solid #e4e4e7', borderRadius: 8 }}>{wip.map(row)}</div>
              </div>
            );
          })()}

          {/* ── SYSTEM ── PostgREST health probe + plain-English cause +
              copy-fix-prompt. Reuses the same anon GET on staff_directory
              that SystemStatus.jsx uses; surfaces the failing URL/code so
              Mo can paste one-click fix prompts into Claude Code. */}
          {tab === 'system' && <SystemTab />}
        </div>
      </div>

      {/* ── USER MODAL (New / Edit) ── */}
      {editUser && (<>
        <div onClick={() => setEditUser(null)} style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.4)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 401, width: 520, maxWidth: '90vw', maxHeight: '85vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e4e4e7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#0a0a0a' }}>{editUser._isNew ? 'New User' : `Edit: ${editUser.name}`}</span>
            <button onClick={() => setEditUser(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#a1a1aa' }}>x</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            <div className="form-group" style={{ marginBottom: 12 }}><label className="form-label">Full Name</label>
              <input className="form-input" value={editUser.name} onChange={e => setEditUser(f => ({ ...f, name: e.target.value }))} autoFocus /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group"><label className="form-label">PIN Code</label>
                <input className="form-input" value={editUser.code} onChange={e => setEditUser(f => ({ ...f, code: e.target.value }))} maxLength={6} /></div>
              <div className="form-group"><label className="form-label">Role</label>
                <select className="form-select" value={editUser.role} onChange={e => setEditUser(f => ({ ...f, role: e.target.value }))}><option value="user">User</option><option value="admin">Admin</option></select></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group"><label className="form-label">Department</label>
                <select className="form-select" value={editUser.department || ''} onChange={e => setEditUser(f => ({ ...f, department: e.target.value }))}>
                  <option value="">Select...</option>{DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select></div>
              <div className="form-group"><label className="form-label">Title</label>
                <input className="form-input" value={editUser.title || ''} onChange={e => setEditUser(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Creative Director" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group"><label className="form-label">Active</label>
                <select className="form-select" value={editUser.active ? 'true' : 'false'} onChange={e => setEditUser(f => ({ ...f, active: e.target.value === 'true' }))}>
                  <option value="true">Active</option><option value="false">Inactive</option>
                </select></div>
              <div className="form-group"><label className="form-label">Timezone</label>
                <select className="form-select" value={editUser.timezone || 'Australia/Sydney'} onChange={e => setEditUser(f => ({ ...f, timezone: e.target.value }))}>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                  <option value="Australia/Melbourne">Melbourne (AEST)</option>
                  <option value="Australia/Brisbane">Brisbane (AEST)</option>
                  <option value="Australia/Perth">Perth (AWST)</option>
                  <option value="America/New_York">New York (EST)</option>
                  <option value="America/Los_Angeles">Los Angeles (PST)</option>
                  <option value="America/Chicago">Chicago (CST)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="Asia/Manila">Manila / Philippines (PHT)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Pacific/Auckland">Auckland (NZST)</option>
                </select></div>
            </div>

            {/* Page Access — grouped */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>Page Access</label>
                {editUser.role !== 'admin' && (
                  <label style={{ fontSize: 10, color: '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox" checked={PAGE_KEYS.every(k => editUser.permissions?.[k] !== false)} onChange={e => { const perms = {}; PAGE_KEYS.forEach(k => { perms[k] = e.target.checked; }); setEditUser(f => ({ ...f, permissions: perms })); }} />
                    Select all
                  </label>
                )}
              </div>

              {/* Main group — page checkboxes + per-page sub-action toggles
                  for pages with PAGE_ACTIONS defined. Sub-actions render
                  indented underneath the page they belong to, and are
                  greyed out (but still tickable) when the page is unticked. */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#52525b' }}>Main</span>
                  {editUser.role !== 'admin' && <button onClick={() => toggleGroupPerms(MAIN_PAGES, true)} style={{ fontSize: 9, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>All</button>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {MAIN_PAGES.map(p => {
                    const isOn = editUser.permissions?.[p.key] !== false;
                    // Home is mandatory for every user — Mo's rule. The
                    // app already opens to Home by default and the nav
                    // item is `universal: true`, so revoking the perm
                    // here would only mislead Mo into thinking it has
                    // an effect. Lock it on for everyone, including
                    // non-admins, alongside the existing admin lock.
                    const isHome = p.key === 'home';
                    const locked = editUser.role === 'admin' || isHome;
                    const subs = PAGE_ACTIONS[p.key] || [];
                    return (
                      <div key={p.key}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, opacity: locked ? 0.5 : 1, cursor: locked ? 'default' : 'pointer', fontWeight: 500 }}>
                          <input type="checkbox" checked={locked || isOn} disabled={locked} onChange={e => setEditUser(f => ({ ...f, permissions: { ...f.permissions, [p.key]: e.target.checked } }))} />
                          {p.label}
                          {isHome && editUser.role !== 'admin' && (
                            <span style={{ fontSize: 9, fontWeight: 500, color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.06em', marginLeft: 2 }}>required</span>
                          )}
                        </label>
                        {subs.length > 0 && (
                          <div style={{ paddingLeft: 22, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {subs.map(sa => {
                              const subOn = editUser.permissions?.[sa.key] !== false;
                              const subLocked = locked;
                              const dimmed = !isOn && !locked;
                              return (
                                <label key={sa.key} style={{
                                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
                                  color: '#52525b',
                                  opacity: subLocked ? 0.5 : (dimmed ? 0.4 : 1),
                                  cursor: subLocked ? 'default' : 'pointer',
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={subLocked || subOn}
                                    disabled={subLocked}
                                    onChange={e => setEditUser(f => ({ ...f, permissions: { ...f.permissions, [sa.key]: e.target.checked } }))}
                                  />
                                  {sa.label}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Construction Site group */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#a1a1aa' }}>Construction Site</span>
                  {editUser.role !== 'admin' && <button onClick={() => toggleGroupPerms(CONSTRUCTION_PAGES, true)} style={{ fontSize: 9, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>All</button>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {CONSTRUCTION_PAGES.map(p => {
                    const isOn = editUser.permissions?.[p.key] === true;
                    const locked = editUser.role === 'admin';
                    return <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, opacity: locked ? 0.5 : 1, cursor: locked ? 'default' : 'pointer' }}>
                      <input type="checkbox" checked={locked || isOn} disabled={locked} onChange={e => setEditUser(f => ({ ...f, permissions: { ...f.permissions, [p.key]: e.target.checked } }))} />{p.label}
                    </label>;
                  })}
                </div>
              </div>

              {/* Special permissions — non-page toggles */}
              <div style={{ paddingTop: 10, borderTop: '1px solid #f4f4f5' }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#52525b', display: 'block', marginBottom: 4 }}>Special</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
                    opacity: editUser.role === 'admin' ? 0.5 : 1,
                    cursor: editUser.role === 'admin' ? 'default' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={editUser.role === 'admin' || editUser.permissions?.mo_chat !== false}
                      disabled={editUser.role === 'admin'}
                      onChange={e => setEditUser(f => ({ ...f, permissions: { ...f.permissions, mo_chat: e.target.checked } }))}
                    />
                    Talk to Mo (⌘M)
                  </label>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
                    opacity: editUser.role === 'admin' ? 0.5 : 1,
                    cursor: editUser.role === 'admin' ? 'default' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={editUser.role === 'admin' || editUser.permissions?.stock_write !== false}
                      disabled={editUser.role === 'admin'}
                      onChange={e => setEditUser(f => ({ ...f, permissions: { ...f.permissions, stock_write: e.target.checked } }))}
                    />
                    Manage stock catalogue (add/edit SKUs)
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditUser(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveUser}>Save</button>
          </div>
        </div>
      </>)}
    </div>
  );
}

// ── ActivityRow ─────────────────────────────────────────────────────────
// Renders a single event from the unified activity feed (login / write /
// llm / activity). Used in both Timeline view and per-user Session view.
// `compact` trims the avatar + uses smaller text for the dense session view.
const KIND_BADGE = {
  login:    { bg: '#ECF6F0', color: '#036E43', label: 'login' },
  write:    { bg: '#EEEBFF', color: '#7c3aed', label: 'write' },
  llm:      { bg: '#FFF7E6', color: '#B85C3C', label: 'llm' },
  activity: { bg: '#F4F4F5', color: '#52525b', label: 'event' },
  export:   { bg: '#E6F4FF', color: '#0B6BB3', label: 'export' },
};

function ActivityRow({ event, resolveUser, compact }) {
  const name = resolveUser(event.actor_code);
  const badge = KIND_BADGE[event.kind] || KIND_BADGE.activity;
  const failed = event.success === false;
  const [copied, setCopied] = useState(false);

  // Mo asked for exact times in the activity log (not "5m ago"). Today
  // → just the HH:MM. Older → DD MMM HH:MM.
  function fmtWhen(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function copyId(e) {
    e.stopPropagation();
    if (!event.eventId) return;
    try { navigator.clipboard?.writeText(event.eventId); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: compact ? '4px 8px' : '8px 12px',
      borderRadius: 8,
      background: failed ? '#FEF2F2' : (compact ? 'transparent' : '#fafafa'),
      borderLeft: failed ? '3px solid #dc2626' : '3px solid transparent',
    }}>
      {!compact && (
        <div style={{ width: 24, height: 24, borderRadius: '50%', background: `hsl(${(name.charCodeAt(0) * 37) % 360}, 45%, 60%)`, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{name.charAt(0)}</div>
      )}
      <span style={{
        fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 999,
        background: badge.bg, color: badge.color, textTransform: 'uppercase',
        letterSpacing: '0.06em', flexShrink: 0, fontFamily: 'JetBrains Mono, ui-monospace, monospace',
      }}>{badge.label}</span>
      {/* Short event ID — Mo can quote this back when investigating issues.
          Click to copy. Highlights briefly to confirm. */}
      {event.eventId && (
        <button
          type="button"
          onClick={copyId}
          title={`Click to copy · source: ${event.sourceTable}`}
          style={{
            fontSize: 9, fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            color: copied ? '#036E43' : '#a1a1aa',
            background: copied ? '#ECF6F0' : 'transparent',
            border: `1px solid ${copied ? '#D6EADF' : '#e4e4e7'}`,
            borderRadius: 4, padding: '1px 5px', cursor: 'pointer',
            flexShrink: 0, letterSpacing: '0.03em',
          }}
        >
          {copied ? 'COPIED' : event.eventId}
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0, fontSize: compact ? 11 : 12, color: '#0a0a0a' }}>
        {!compact && <span style={{ fontWeight: 500 }}>{name}</span>}
        {!compact && ' '}
        <span style={{ color: '#52525b' }}>{event.label}</span>
        {event.detail && <span style={{ color: '#7c3aed', marginLeft: 4 }}>· {event.detail.length > 80 ? event.detail.slice(0, 80) + '…' : event.detail}</span>}
        {failed && <span style={{ color: '#dc2626', marginLeft: 6, fontWeight: 600 }}>· FAILED</span>}
      </div>
      {event.ip && <span style={{ fontSize: 9, color: '#a1a1aa', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{event.ip}</span>}
      <span style={{ fontSize: 10, color: '#a1a1aa', flexShrink: 0 }}>{fmtWhen(event.when)}</span>
    </div>
  );
}
